import { useEffect, useMemo, useState } from "react";
import { api, formatarErroApi } from "@/api";
import { LayoutAutenticado } from "@/componentes/LayoutAutenticado";
import { CartaoJogo } from "@/componentes/CartaoJogo";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function Palpitar() {
  const [jogos, setJogos] = useState([]);
  const [palpites, setPalpites] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [filtroRodada, setFiltroRodada] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("");
  const [modoFiltro, setModoFiltro] = useState("rodadas"); // "rodadas" ou "grupos"
  const [modal, setModal] = useState(null); // {jogo, palpite}

  const carregar = async () => {
    setCarregando(true);
    try {
      const [j, p] = await Promise.all([api.get("/jogos"), api.get("/palpites/meus")]);
      setJogos(j.data);
      const map = {};
      (p.data || []).forEach((pp) => { map[pp.jogoId] = pp; });
      setPalpites(map);
    } catch (e) {
      toast.error(formatarErroApi(e));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const rodadas = useMemo(() => Array.from(new Set(jogos.map((j) => j.rodada))).sort((a, b) => a - b), [jogos]);
  const grupos = useMemo(() => {
    const gruposUnicos = Array.from(new Set(jogos.map((j) => j.grupo?.id).filter(Boolean))).map(id => jogos.find(j => j.grupo?.id === id)?.grupo).filter(Boolean);
    return gruposUnicos.sort((a, b) => {
      const nomeA = a.nome.replace("Grupo ", "").trim();
      const nomeB = b.nome.replace("Grupo ", "").trim();
      return nomeA.localeCompare(nomeB);
    });
  }, [jogos]);
  
  const filtrados = useMemo(() => {
    if (modoFiltro === "rodadas") {
      return filtroRodada ? jogos.filter((j) => j.rodada === Number(filtroRodada)) : jogos;
    } else {
      return filtroGrupo ? jogos.filter((j) => j.grupo?.id === filtroGrupo) : jogos;
    }
  }, [jogos, filtroRodada, filtroGrupo, modoFiltro]);

  return (
    <LayoutAutenticado>
      <div data-testid="pagina-palpitar">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Apostar</div>
            <h1 className="font-heading text-4xl font-bold uppercase mt-1">Faça seus palpites</h1>
            <p className="text-sm text-white/60 mt-1">Palpites são aceitos até 2h antes de cada partida.</p>
          </div>
          <div className="flex gap-2">
            <select
              value={modoFiltro === "rodadas" ? filtroRodada : filtroGrupo}
              onChange={(e) => {
                if (modoFiltro === "rodadas") {
                  setFiltroRodada(e.target.value);
                } else {
                  setFiltroGrupo(e.target.value);
                }
              }}
              className="surface-card px-4 py-2.5 rounded-md text-sm border border-white/10 focus:border-verdebrasil focus:outline-none"
              data-testid="select-filtro"
            >
              {modoFiltro === "rodadas" ? (
                <>
                  <option value="">Todas as rodadas</option>
                  {rodadas.map((r) => (
                    <option key={r} value={String(r)}>{"Rodada " + r}</option>
                  ))}
                </>
              ) : (
                <>
                  <option value="">Todos os grupos</option>
                  {grupos.map((g) => (
                    <option key={g.id} value={g.id}>{g.nome}</option>
                  ))}
                </>
              )}
            </select>
            <button
              onClick={() => {
                setModoFiltro(modoFiltro === "rodadas" ? "grupos" : "rodadas");
                setFiltroRodada("");
                setFiltroGrupo("");
              }}
              className="px-4 py-2.5 rounded-md text-sm font-bold bg-amarelobrasil text-black hover:bg-amarelobrasil/80 transition-all"
              data-testid="btn-alternar-filtro"
            >
              {modoFiltro === "rodadas" ? "Por Grupo" : "Por Rodada"}
            </button>
          </div>
        </div>

        {carregando ? (
          <div className="text-white/40">Carregando jogos...</div>
        ) : filtrados.length === 0 ? (
          <div className="surface-card rounded-2xl p-10 text-center text-white/50" data-testid="vazio-jogos">
            Nenhum jogo agendado ainda. A administração precisa cadastrar os jogos.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtrados.map((j) => (
              <CartaoJogo
                key={j.id}
                jogo={j}
                palpiteAtual={palpites[j.id]}
                onPalpitar={(jogo, p) => setModal({ jogo, palpite: p })}
              />
            ))}
          </div>
        )}
      </div>

      {modal && (
        <ModalPalpite
          jogo={modal.jogo}
          palpiteExistente={modal.palpite}
          onFechar={() => setModal(null)}
          onSalvar={async () => {
            await carregar();
            setModal(null);
          }}
        />
      )}
    </LayoutAutenticado>
  );
}

function ModalPalpite({ jogo, palpiteExistente, onFechar, onSalvar }) {
  const [gm, setGm] = useState(palpiteExistente?.golsMandantePalpite ?? 0);
  const [gv, setGv] = useState(palpiteExistente?.golsVisitantePalpite ?? 0);
  const [enviando, setEnviando] = useState(false);

  const submeter = async () => {
    setEnviando(true);
    try {
      await api.post("/palpites", {
        jogoId: jogo.id,
        golsMandantePalpite: Number(gm),
        golsVisitantePalpite: Number(gv),
      });
      toast.success("Palpite salvo!");
      onSalvar();
    } catch (e) {
      toast.error(formatarErroApi(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="modal-palpite">
      <div className="surface-card rounded-2xl p-6 md:p-8 max-w-md w-full relative">
        <button onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white" data-testid="btn-fechar-modal">
          <X className="w-5 h-5" />
        </button>
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil mb-2">
          Rodada {jogo.rodada}
        </div>
        <h2 className="font-heading uppercase font-bold text-2xl">
          {jogo.selecaoMandante?.nome} <span className="text-white/40">×</span> {jogo.selecaoVisitante?.nome}
        </h2>
        <div className="text-xs text-white/60 mt-1">
          {new Date(jogo.dataHoraJogo).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
        </div>

        <div className="mt-8 grid grid-cols-3 items-center gap-4">
          <SpinNumero label={jogo.selecaoMandante?.sigla} valor={gm} setValor={setGm} testId="spin-gm" />
          <div className="text-center font-heading font-black text-3xl text-white/40">×</div>
          <SpinNumero label={jogo.selecaoVisitante?.sigla} valor={gv} setValor={setGv} testId="spin-gv" />
        </div>

        <button
          onClick={submeter}
          disabled={enviando}
          className="mt-8 w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark transition-all hover:glow-green disabled:opacity-50"
          data-testid="btn-salvar-palpite"
        >
          {enviando ? "Salvando..." : palpiteExistente ? "Atualizar Palpite" : "Salvar Palpite"}
        </button>
      </div>
    </div>
  );
}

function SpinNumero({ label, valor, setValor, testId }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold mb-2">{label}</div>
      <div className="surface-elev rounded-xl p-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setValor(Math.max(0, Number(valor) - 1))}
          className="w-8 h-8 rounded-md hover:bg-white/10 font-heading font-bold"
          data-testid={`${testId}-menos`}
        >−</button>
        <input
          type="number"
          min={0}
          value={valor}
          onChange={(e) => setValor(Math.max(0, Number(e.target.value || 0)))}
          className="w-12 bg-transparent text-center font-heading font-black text-3xl outline-none"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setValor(Number(valor) + 1)}
          className="w-8 h-8 rounded-md hover:bg-white/10 font-heading font-bold"
          data-testid={`${testId}-mais`}
        >+</button>
      </div>
    </div>
  );
}
